/* DNAcademy classroom player.
   Gated behind DNA.auth; renders the lesson list, the YouTube player (video lessons)
   and the block renderer (DNA.blocks, assets/player-blocks.js) for one course.
   All strings coming back from the API land in textContent — never innerHTML;
   sanitised rich-text bodies are handled inside player-blocks.js only. */
(() => {
  const qs = (sel) => document.querySelector(sel);

  const loadYouTubeAPI = () => new Promise((resolve) => {
    if (window.YT && window.YT.Player) { resolve(window.YT); return; }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') prev();
      resolve(window.YT);
    };
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const s = document.createElement('script');
      s.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(s);
    }
  });

  const init = async () => {
    const A = window.DNA && window.DNA.auth;
    if (!A) return;
    if (!(await A.requireAuth())) return;

    const params = new URLSearchParams(location.search);
    const courseHandle = params.get('course');
    if (!courseHandle) { location.href = '/pages/courses'; return; }
    // player-blocks.js is a deferred script before this one; init runs after DOMContentLoaded.
    const blocks = window.DNA.blocks || null;

    const loading = qs('[data-classroom-loading]');
    const errorEl = qs('[data-classroom-error]');
    const fail = (msg) => {
      loading.hidden = true;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    };

    let data = null;
    try {
      const res = await A.api('/api/courses/' + encodeURIComponent(courseHandle) + '/lessons');
      if (res.status === 403) { location.href = '/pages/courses/' + encodeURIComponent(courseHandle); return; }
      if (res.status === 401) { A.clear(); await A.requireAuth(); return; }
      if (!res.ok) { fail('We couldn’t load this course — please refresh to try again.'); return; }
      data = await res.json();
    } catch (_) {
      fail('We couldn’t load this course — please refresh to try again.');
      return;
    }
    const lessons = Array.isArray(data.lessons) ? data.lessons : [];
    if (lessons.length === 0) { fail('This course has no lessons yet.'); return; }

    // Course title from the public catalog embed; the API only returns the handle.
    let courseTitle = courseHandle.replace(/-/g, ' ');
    try {
      const catalog = JSON.parse(qs('[data-course-catalog]').textContent) || [];
      const hit = catalog.filter((c) => c.handle === courseHandle)[0];
      if (hit && hit.title) courseTitle = hit.title;
    } catch (_) {}
    qs('[data-course-title]').textContent = courseTitle;

    const listEl = qs('[data-lesson-list]');
    const titleEl = qs('[data-lesson-title]');
    const frameEl = qs('[data-player-frame]');
    const mountEl = qs('[data-player-mount]');
    const blocksEl = qs('[data-blocks-mount]');
    const emptyEl = qs('[data-lesson-empty]');
    const bannerEl = qs('[data-course-complete]');
    const prevBtn = qs('[data-prev]');
    const nextBtn = qs('[data-next]');
    const completeBtn = qs('[data-complete]');
    const mainEl = qs('.classroom__main');
    const rows = [];
    let current = -1;
    let player = null;
    let playerReady = false;
    let ticker = null;
    let loadSeq = 0;

    const YT_ID = /^[A-Za-z0-9_-]{6,20}$/;
    const hasVideo = (lesson) => !!(lesson && YT_ID.test(lesson.youtube_id || ''));
    const isQuiz = (lesson) => !!(lesson && lesson.kind === 'quiz');

    // ---- sidebar ----
    // The course's "Show lesson numbers" (plan N4): only an explicit false hides them.
    const numberOf = (i) => (data.show_lesson_count === false ? '' : String(i + 1));
    // "Lessons in order" (N3): finishing a lesson may open the next one, so the
    // way on waits for the Worker's fresh locks instead of a stale lock panel.
    const inOrder = data.restrict_navigation === true;
    lessons.forEach((lesson, i) => {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'classroom__lesson' + (isQuiz(lesson) ? ' classroom__lesson--quiz' : '');
      const num = document.createElement('span');
      num.className = 'classroom__num';
      num.textContent = numberOf(i);
      const title = document.createElement('span');
      title.className = 'classroom__ltitle';
      title.textContent = lesson.title || lesson.handle;
      if (isQuiz(lesson)) {
        const qi = document.createElement('span');
        qi.className = 'classroom__quiz';
        qi.textContent = 'Quiz';
        qi.setAttribute('aria-label', 'Quiz');
        title.appendChild(qi);
      }
      const dur = document.createElement('span');
      dur.className = 'meta';
      dur.textContent = lesson.duration_minutes ? lesson.duration_minutes + ' min' : '';
      const check = document.createElement('span');
      check.className = 'classroom__check';
      check.textContent = '✓';
      check.setAttribute('aria-label', 'Completed');
      check.hidden = !lesson.completed;
      btn.append(num, title, dur, check);
      btn.addEventListener('click', () => { if (i !== current) select(i); });
      li.appendChild(btn);
      listEl.appendChild(li);
      rows.push({ btn, check, num, locked: false });
    });

    // ---- locked lessons (plan D8, D9, N3): a lesson after a "require a passing
    // score" quiz the student has not passed, or (lessons in order) after an
    // unfinished lesson, stays in the list, dimmed, with a lock in place of its
    // number. An SVG, not an emoji: the site has none. ----
    const SVG_NS = 'http://www.w3.org/2000/svg';
    const lockIcon = () => {
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('class', 'classroom__lock');
      svg.setAttribute('viewBox', '0 0 16 16');
      svg.setAttribute('role', 'img');
      svg.setAttribute('aria-label', 'Locked');
      const body = document.createElementNS(SVG_NS, 'rect');
      body.setAttribute('x', '3'); body.setAttribute('y', '7');
      body.setAttribute('width', '10'); body.setAttribute('height', '7'); body.setAttribute('rx', '1.5');
      const shackle = document.createElementNS(SVG_NS, 'path');
      shackle.setAttribute('d', 'M5.5 7V5a2.5 2.5 0 0 1 5 0v2');
      svg.append(body, shackle);
      return svg;
    };
    // Repaints a row only when its lock changes, so an unlocked row is never touched.
    const paintLock = (row, lesson, i) => {
      const locked = !!lesson.locked;
      if (row.locked === locked) return;
      row.locked = locked;
      row.btn.classList.toggle('is-locked', locked);
      row.num.textContent = locked ? '' : numberOf(i);
      if (locked) row.num.appendChild(lockIcon());
    };

    const refreshUI = () => {
      const lesson = lessons[current];
      rows.forEach((row, i) => {
        row.btn.classList.toggle('is-active', i === current);
        row.check.hidden = !lessons[i].completed;
        paintLock(row, lessons[i], i);
      });
      titleEl.textContent = lesson.title || lesson.handle;
      prevBtn.disabled = current === 0;
      nextBtn.disabled = current === lessons.length - 1;
      completeBtn.textContent = lesson.completed ? 'Completed ✓' : 'Mark complete';
      // Quizzes complete by passing; a locked lesson cannot be done yet.
      completeBtn.hidden = isQuiz(lesson) || !!lesson.locked;
      if (bannerEl) bannerEl.hidden = !lessons.every((l) => !!l.completed);
      // On the last lesson the bar depends on what is complete (slice 4a D1).
      paintBar();
    };

    // ---- progress ----
    const currentTime = () => {
      if (player && playerReady && hasVideo(lessons[current]) && typeof player.getCurrentTime === 'function') {
        const t = Math.floor(player.getCurrentTime());
        if (Number.isFinite(t) && t >= 0) return t;
      }
      return 0;
    };

    const postProgress = (lesson, completedFlag) => {
      const body = {
        course_handle: courseHandle,
        lesson_handle: lesson.handle
      };
      // position_seconds only for lessons with a YouTube player — sending it
      // for block lessons would overwrite a saved position with 0.
      if (hasVideo(lesson)) {
        const pos = currentTime();
        lesson.position_seconds = pos;
        body.position_seconds = pos;
      }
      // completed is only sent on explicit user intent (toggle / video ended);
      // position beacons omit it so they can never revert completion state
      // saved from another device.
      if (typeof completedFlag === 'boolean') body.completed = completedFlag;
      return A.api('/api/progress', {
        method: 'POST',
        body: JSON.stringify(body)
      }).catch(() => {});
    };

    const stopTicker = () => { if (ticker) { clearInterval(ticker); ticker = null; } };
    const startTicker = () => {
      stopTicker();
      ticker = setInterval(() => {
        postProgress(lessons[current]);
      }, 15000);
    };

    // ---- YouTube player ----
    const startSecondsFor = (lesson) =>
      lesson.position_seconds > 5 ? lesson.position_seconds : 0;

    const onStateChange = (e) => {
      const lesson = lessons[current];
      if (!hasVideo(lesson)) return;
      if (e.data === YT.PlayerState.PLAYING) {
        startTicker();
      } else if (e.data === YT.PlayerState.PAUSED) {
        stopTicker();
        postProgress(lesson);
      } else if (e.data === YT.PlayerState.ENDED) {
        stopTicker();
        // Video-only lessons complete when the video ends; block lessons
        // with a header video complete through their Continue chain.
        if (!lesson._hasBlocks) {
          lesson.completed = true;
          refreshUI();
          // The next lesson may open once this completion is saved (N3).
          const saved = postProgress(lesson, true).then(refreshLocks);
          if (current < lessons.length - 1) {
            if (!inOrder) select(current + 1);
            else saved.then(() => { if (lesson === lessons[current]) select(current + 1); });
          }
        }
      }
    };

    let playerCreating = null;
    let createdVideoId = null;
    // Make the player show the lesson the student is on NOW (they may have moved
    // on while the IFrame API was loading).
    const syncPlayerToCurrent = () => {
      const cur = lessons[current];
      if (!hasVideo(cur)) { hideVideo(); return; }
      frameEl.hidden = false;
      if (cur.youtube_id !== createdVideoId) {
        createdVideoId = cur.youtube_id;
        player.loadVideoById({ videoId: cur.youtube_id, startSeconds: startSecondsFor(cur) });
      } else {
        const pos = startSecondsFor(cur);
        if (pos > 5) player.seekTo(pos, true);
      }
    };
    const createPlayer = (lesson) => {
      if (playerCreating) return playerCreating;
      createdVideoId = lesson.youtube_id;
      playerCreating = loadYouTubeAPI().then((YTApi) => new Promise((resolve) => {
        const start = startSecondsFor(lesson);
        player = new YTApi.Player(mountEl, {
          host: 'https://www.youtube-nocookie.com',
          videoId: lesson.youtube_id,
          playerVars: { rel: 0, modestbranding: 1, start },
          events: {
            onReady: () => {
              playerReady = true;
              syncPlayerToCurrent();
              resolve(player);
            },
            onStateChange
          }
        });
      }));
      return playerCreating;
    };

    const showVideo = async (lesson) => {
      frameEl.hidden = false;
      if (!playerCreating) {
        await createPlayer(lesson);
      } else if (!playerReady) {
        await playerCreating; // onReady already synced to the current lesson
      } else if (lesson.youtube_id !== createdVideoId) {
        createdVideoId = lesson.youtube_id;
        player.loadVideoById({ videoId: lesson.youtube_id, startSeconds: startSecondsFor(lesson) });
      }
    };
    const hideVideo = () => {
      frameEl.hidden = true;
      if (player && playerReady && typeof player.stopVideo === 'function') {
        try { player.stopVideo(); } catch (_) {}
      }
    };

    // ---- the bar under the lesson (Stage B2, D15; slice 4a D1): the way on,
    // shown once the lesson is fully revealed. One button, made once; what it
    // says and where it goes follow the course. ----
    const nextBar = document.createElement('button');
    nextBar.type = 'button';
    nextBar.className = 'classroom__nextbar';
    nextBar.setAttribute('data-next-bar', '');
    nextBar.hidden = true;
    const nextLabel = document.createElement('span');
    nextLabel.className = 'meta classroom__nextbar-label';
    nextLabel.textContent = 'Next lesson';
    const nextTitle = document.createElement('span');
    nextTitle.className = 'classroom__nextbar-title';
    const nextArrow = document.createElement('span');
    nextArrow.className = 'classroom__nextbar-arrow';
    nextArrow.setAttribute('aria-hidden', 'true');
    nextArrow.textContent = '→';
    nextBar.append(nextLabel, nextTitle, nextArrow);
    blocksEl.after(nextBar);
    let revealed = false; // the lesson on screen has nothing left to reveal
    let barGo = null; // what a click on the bar does now
    const titleOf = (lesson) => lesson.title || lesson.handle;
    // Where the bar leads from the lesson on screen, or null for no bar: the
    // next lesson; on the last one, the first other lesson still to finish,
    // else the way out once this one is done too (Patrick, 2026-09-24).
    const wayOn = () => {
      if (current < lessons.length - 1) {
        return { label: 'Next lesson', title: titleOf(lessons[current + 1]), go: () => goTo(current + 1) };
      }
      const left = lessons.findIndex((l, i) => i !== current && !l.completed);
      if (left !== -1) return { label: 'Still to finish', title: titleOf(lessons[left]), go: () => goTo(left) };
      if (!lessons[current].completed) return null;
      return { label: 'Course complete', title: 'Back to my courses', go: () => { location.href = '/pages/my-courses'; } };
    };
    // Never over a lock panel (a locked NEXT lesson is fine).
    const paintBar = () => {
      const way = revealed && !lessons[current].locked ? wayOn() : null;
      barGo = way ? way.go : null;
      nextBar.hidden = !way;
      if (!way) return;
      nextLabel.textContent = way.label;
      nextTitle.textContent = way.title;
      nextBar.setAttribute('aria-label', way.label + ': ' + way.title);
    };
    const setNextBar = (show) => {
      revealed = !!show;
      paintBar();
    };

    // ---- lesson content (blocks / quiz) ----
    // In the lesson area instead of the lesson: which lesson opens it, and a way
    // there. A "require a passing score" quiz must be passed; any other locker
    // (lessons in order, N4) must be finished.
    const showLockPanel = (lesson) => {
      blocksEl.textContent = '';
      blocksEl.className = 'blocks';
      blocksEl.removeAttribute('aria-busy');
      emptyEl.hidden = true;
      const lockerIdx = lessons.map((l) => l.handle).indexOf(lesson.locked_by);
      const locker = lessons[lockerIdx];
      // A Worker from before N3 sends no require_pass: then only such a quiz locks.
      const mustPass = !locker || (isQuiz(locker) && locker.require_pass !== false);
      const panel = document.createElement('div');
      panel.className = 'classroom__locked';
      const text = document.createElement('p');
      text.className = 'classroom__locked-text';
      text.textContent = (mustPass ? 'Pass “' : 'Finish “') +
        (locker ? (locker.title || locker.handle) : (lesson.locked_by || 'the quiz')) + '” to unlock this lesson.';
      panel.appendChild(text);
      if (locker) {
        const go = document.createElement('button');
        go.type = 'button';
        go.className = 'btn btn--solid';
        go.textContent = mustPass ? 'Go to the quiz' : 'Go to the lesson';
        go.addEventListener('click', () => {
          select(lockerIdx);
          // This button is gone with the panel: keep focus beside the lesson it opened.
          titleEl.tabIndex = -1;
          titleEl.focus();
        });
        panel.appendChild(go);
      }
      blocksEl.appendChild(panel);
    };

    // A quiz attempt or a saved completion can open the lessons it locked (D8,
    // N3): ask the Worker again. Only the lock fields are taken, so nothing else
    // in the list can regress; only the latest ask is applied, so an older
    // answer landing late cannot lock a lesson again.
    let lockSeq = 0;
    const refreshLocks = async () => {
      const seq = ++lockSeq;
      let fresh = null;
      try {
        const res = await A.api('/api/courses/' + encodeURIComponent(courseHandle) + '/lessons');
        if (res.ok) fresh = await res.json();
      } catch (_) { fresh = null; }
      if (seq !== lockSeq || !fresh || !Array.isArray(fresh.lessons)) return;
      const wasLocked = !!lessons[current].locked;
      fresh.lessons.forEach((f) => {
        const lesson = lessons.filter((l) => l.handle === f.handle)[0];
        if (!lesson) return;
        lesson.locked = !!f.locked;
        lesson.locked_by = f.locked_by || null;
      });
      // The lesson on screen opened: load it in place of its lock panel. Only
      // on that change, so select's own refetch of a locked lesson cannot loop.
      if (wasLocked && !lessons[current].locked) select(current);
      else refreshUI();
    };

    const onBlockProgress = (lesson) => (evt) => {
      // Events arrive once the Worker has saved them. Any quiz attempt (a failed
      // one finishes the quiz, N3) or a lesson just completed can open others.
      const opens = !!evt && (evt.type === 'quiz' || (!!evt.completed && !lesson.completed));
      if (evt && evt.completed) lesson.completed = true;
      refreshUI();
      const locks = opens ? refreshLocks() : null;
      // A saved Continue (D14/D15): the last block opens the next lesson; the one
      // that reveals everything shows the bar. Only for the lesson on screen, so
      // a save landing after the student moved on cannot move them again.
      if (!evt || evt.type !== 'continue' || lesson !== lessons[current]) return;
      if (evt.last && current < lessons.length - 1) {
        if (!locks || !inOrder) goTo(current + 1);
        else locks.then(() => { if (lesson === lessons[current]) goTo(current + 1); });
      }
      else if (evt.revealedAll) setNextBar(true);
    };

    const renderLesson = async (lesson, seq) => {
      blocksEl.textContent = '';
      blocksEl.className = 'blocks';
      blocksEl.setAttribute('aria-busy', 'true');
      emptyEl.hidden = true;
      let detail = null;
      try {
        const res = await A.api('/api/courses/' + encodeURIComponent(courseHandle) + '/lessons/' + encodeURIComponent(lesson.handle));
        if (res.status === 401) { A.clear(); await A.requireAuth(); return; }
        if (res.status === 403) {
          // Locked since the list was drawn (D8): the Worker names the quiz.
          const body = await res.json().catch(() => null);
          if (seq !== loadSeq) return;
          if (body && body.error === 'locked') {
            lesson.locked = true;
            lesson.locked_by = body.locked_by || null;
            // A YouTube player still loading must not show it behind the panel.
            lesson.youtube_id = null;
            refreshUI();
            hideVideo();
            showLockPanel(lesson);
            return;
          }
        }
        if (res.ok) detail = await res.json();
      } catch (_) { detail = null; }
      if (seq !== loadSeq) return; // user moved on
      blocksEl.removeAttribute('aria-busy');

      // The lesson payload is authoritative for the video id (list may be stale).
      if (detail && Object.prototype.hasOwnProperty.call(detail, 'youtube_id') && (detail.youtube_id || null) !== (lesson.youtube_id || null)) {
        lesson.youtube_id = detail.youtube_id || null;
        if (hasVideo(lesson)) showVideo(lesson); else hideVideo();
      }
      const kind = detail && detail.kind === 'quiz' ? 'quiz' : 'content';
      const blockCount = detail && detail.content && Array.isArray(detail.content.blocks) ? detail.content.blocks.length : 0;
      const questionCount = detail && detail.content && Array.isArray(detail.content.questions) ? detail.content.questions.length : 0;
      const hasContent = kind === 'quiz' ? questionCount > 0 || !!detail : blockCount > 0;
      lesson._hasBlocks = kind === 'content' && blockCount > 0;

      if (!blocks || !detail || (!hasContent && !hasVideo(lesson))) {
        if (!hasVideo(lesson)) {
          emptyEl.hidden = false;
          emptyEl.textContent = detail
            ? 'This lesson doesn’t have any content yet — check back soon.'
            : 'We couldn’t load this lesson — please refresh to try again.';
        }
        setNextBar(true); // nothing to reveal
        return;
      }
      if (!hasContent) { setNextBar(true); return; } // video-only lesson

      const payload = Object.assign({}, detail, { completed: !!lesson.completed });
      const info = blocks.renderLesson(blocksEl, payload, {
        course: courseHandle,
        lesson: lesson.handle,
        api: A.api,
        onProgress: onBlockProgress(lesson)
      });
      // A player that does not say (a quiz, an older player) has nothing left to reveal.
      setNextBar(!info || info.revealedAll !== false);
      // Resume: scroll to the first incomplete segment when the student is part-way.
      if (info && info.resumeTarget && !hasVideo(lesson)) {
        requestAnimationFrame(() => info.resumeTarget.scrollIntoView({ behavior: 'auto', block: 'start' }));
      }
    };

    const select = (i) => {
      if (i < 0 || i >= lessons.length) return;
      stopTicker();
      if (current !== -1 && hasVideo(lessons[current])) postProgress(lessons[current]);
      current = i;
      setNextBar(false); // until this lesson is revealed
      const lesson = lessons[i];
      history.replaceState(null, '', '#lesson=' + encodeURIComponent(lesson.handle));
      refreshUI();
      loadSeq += 1;
      // Never fetched while locked (the Worker would refuse it). The list
      // withholds a locked lesson's video id (C4); hideVideo clears the
      // previous lesson's player. Asking for the list again lets a refetch that
      // failed after a pass recover on the next click of a locked row.
      if (lesson.locked) { hideVideo(); showLockPanel(lesson); refreshLocks(); return; }
      if (hasVideo(lesson)) showVideo(lesson); else hideVideo();
      renderLesson(lesson, loadSeq);
    };

    // The bar or a trailing Continue (D14/D15): a lesson, with focus on its
    // title and the view at its top, as if the student had just opened it.
    const goTo = (i) => {
      select(i);
      titleEl.tabIndex = -1;
      titleEl.focus({ preventScroll: true });
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      mainEl.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    };

    // ---- controls ----
    prevBtn.addEventListener('click', () => select(current - 1));
    nextBtn.addEventListener('click', () => select(current + 1));
    nextBar.addEventListener('click', () => { if (barGo) barGo(); });
    // Slice 5 (D11): a link to another lesson of this course (Studio writes
    // `#lesson=<handle>` into rich text and into Button blocks) opens it here.
    // A handle this course does not have does nothing; a modifier-click keeps
    // the browser's own behaviour (a new tab reads the hash on load).
    blocksEl.addEventListener('click', (e) => {
      const a = e.target && e.target.closest ? e.target.closest('a[href^="#lesson="]') : null;
      if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      let handle = null;
      try { handle = decodeURIComponent(a.getAttribute('href').slice('#lesson='.length)); } catch (_) { return; }
      const idx = lessons.map((l) => l.handle).indexOf(handle);
      if (idx !== -1) goTo(idx);
    });
    completeBtn.addEventListener('click', () => {
      const lesson = lessons[current];
      lesson.completed = !lesson.completed;
      refreshUI();
      // Finishing (or un-finishing) a lesson can change what is locked (N3).
      postProgress(lesson, lesson.completed).then(refreshLocks);
    });

    // ---- initial lesson: hash first, else first incomplete, else the first ----
    let initial = 0;
    const hashMatch = location.hash.match(/lesson=([^&]+)/);
    if (hashMatch) {
      const handle = decodeURIComponent(hashMatch[1]);
      const idx = lessons.map((l) => l.handle).indexOf(handle);
      if (idx !== -1) initial = idx;
    } else {
      const firstIncomplete = lessons.map((l) => !!l.completed).indexOf(false);
      if (firstIncomplete !== -1) initial = firstIncomplete;
    }

    loading.hidden = true;
    qs('[data-classroom]').hidden = false;
    select(initial);

    // Flush a final position when the tab is hidden or closed mid-play.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && current !== -1 && ticker) {
        postProgress(lessons[current]);
      }
    });
  };

  // This deferred script sits before auth.js in document order, so DNA.auth
  // may not exist yet; DOMContentLoaded fires after ALL deferred scripts run.
  if (window.DNA && window.DNA.auth) {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
